import { useCallback, useState } from 'react';
import { VehicleRowGroup } from 'src/pages/ConvenienceDispatches/adapter';

/** 車両・車番・ドライバーの変更を、サーバ反映を待たずに見せるための表示上書き */
export type VehicleDisplayOverride = { truckName?: string; plateNumber?: string; driverName?: string };

/**
 * 表示上書きを行グループへ被せる。キーは勤務 (shift_truck.id)。
 *
 * truckId をキーにすると、1 台に 2 つの勤務が乗るとき片方のドライバーを変えただけで
 * もう 1 つの勤務にも新しい名前が出る。
 *
 * 上書きが無いときに同じ配列参照をそのまま返すのが要点。新しい配列を返すと、
 * memo 化した車両リストが毎回まるごと再描画される。
 */
export function applyVehicleOverrides(
  groups: VehicleRowGroup[],
  overrides: ReadonlyMap<number, VehicleDisplayOverride>,
): VehicleRowGroup[] {
  if (overrides.size === 0) return groups;
  return groups.map((group) => {
    const override = group.shiftTruckId != null ? overrides.get(group.shiftTruckId) : undefined;
    return override ? { ...group, vehicle: { ...group.vehicle, ...override } } : group;
  });
}

/**
 * 変更を出してからサーバ反映までの間、表示だけ先に切り替えるための state。
 *
 * mutation と同時に上書きを置き、refetch 完了後に消す。サーバが同じ値を返していれば
 * 消しても見た目は変わらない（ちらつかない）。失敗時も消せば元の値に戻る。
 */
export function useVehicleDisplayOverrides() {
  const [overrides, setOverrides] = useState<Map<number, VehicleDisplayOverride>>(new Map());

  const setVehicleOverride = useCallback((shiftTruckId: number, override: VehicleDisplayOverride) => {
    setOverrides((prev) => {
      const next = new Map(prev);
      next.set(shiftTruckId, { ...next.get(shiftTruckId), ...override });
      return next;
    });
  }, []);

  const clearVehicleOverride = useCallback((shiftTruckId: number) => {
    setOverrides((prev) => {
      if (!prev.has(shiftTruckId)) return prev;
      const next = new Map(prev);
      next.delete(shiftTruckId);
      return next;
    });
  }, []);

  return { overrides, setVehicleOverride, clearVehicleOverride };
}
